/**
 * Validation utilities
 */

export function validateEmail(email: string): { valid: boolean; error?: string } {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!email) {
    return { valid: false, error: 'Email is required' };
  }

  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Invalid email format' };
  }

  return { valid: true };
}

export function validatePassword(password: string): { valid: boolean; error?: string } {
  if (!password) {
    return { valid: false, error: 'Password is required' };
  }

  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters' };
  }

  if (!/[A-Z]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one uppercase letter' };
  }

  if (!/[0-9]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one number' };
  }

  return { valid: true };
}

export function validatePlayerName(name: string): { valid: boolean; error?: string } {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Player name is required' };
  }

  if (name.length < 2 || name.length > 50) {
    return { valid: false, error: 'Player name must be between 2 and 50 characters' };
  }

  // Only letters, spaces, and hyphens
  if (!/^[a-zA-Z\s-]+$/.test(name)) {
    return { valid: false, error: 'Player name can only contain letters, spaces, and hyphens' };
  }

  return { valid: true };
}

/**
 * Calculate Levenshtein distance between two strings
 * Used for detecting similar player names
 */
function levenshteinDistance(str1: string, str2: string): number {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();

  const m = s1.length;
  const n = s2.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (s1[i - 1] === s2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j - 1] + 1, // substitution
          dp[i - 1][j] + 1, // deletion
          dp[i][j - 1] + 1 // insertion
        );
      }
    }
  }

  return dp[m][n];
}

export function findSimilarNames(
  newName: string,
  existingNames: string[]
): string[] {
  const threshold = 3;
  const similar: string[] = [];

  for (const existingName of existingNames) {
    const distance = levenshteinDistance(newName, existingName);
    if (distance < threshold && distance > 0) {
      similar.push(existingName);
    }
  }

  return similar;
}

export function validateScore(score: any): { valid: boolean; error?: string } {
  if (score === null || score === undefined || score === '') {
    return { valid: true }; // Optional field
  }

  const num = Number(score);

  if (isNaN(num)) {
    return { valid: false, error: 'Score must be a number' };
  }

  if (!Number.isInteger(num)) {
    return { valid: false, error: 'Score must be an integer' };
  }

  if (num < 0 || num > 99) {
    return { valid: false, error: 'Score must be between 0 and 99' };
  }

  return { valid: true };
}
